import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/AggiornaMovimentiCassa.css";

const API_URL = process.env.REACT_APP_API_URL;

const TIPI_MOVIMENTO = [
  { value: "", label: "Tutti" },
  { value: "ENTRATA", label: "Entrata" },
  { value: "USCITA", label: "Uscita" },
];

const METODI_PAGAMENTO = [
  { value: "CONTANTI", label: "Contanti" },
  { value: "BONIFICO", label: "Bonifico" },
  { value: "POS", label: "POS" },
  { value: "ASSEGNO", label: "Assegno" },
  { value: "ALTRO", label: "Altro" },
];

const CATEGORIE = [
  { value: "", label: "Tutte" },
  { value: "QUOTE", label: "Quote" },
  { value: "SPONSOR", label: "Sponsor" },
  { value: "CONTRIBUTI", label: "Contributi" },
  { value: "EVENTI", label: "Eventi" },
  { value: "RIMBORSI", label: "Rimborsi" },
  { value: "ALTRE_ENTRATE", label: "Altre entrate" },
  { value: "AFFITTO_CAMPI", label: "Affitto campi" },
  { value: "MATERIALE_SPORTIVO", label: "Materiale sportivo" },
  { value: "ARBITRI", label: "Arbitri" },
  { value: "RIMBORSI_STAFF", label: "Rimborsi staff" },
  { value: "VISITE_MEDICHE", label: "Visite mediche" },
  { value: "TASSE", label: "Tasse" },
  { value: "FEDERAZIONE", label: "Federazione" },
  { value: "MANUTENZIONE", label: "Manutenzione" },
  { value: "UTENZE", label: "Utenze" },
  { value: "ALTRE_USCITE", label: "Altre uscite" },
];

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    background: "rgba(255,255,255,0.10)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    borderColor: state.isFocused
      ? "rgba(255,255,255,0.40)"
      : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 12px",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    background: "rgba(15, 23, 42, 0.96)",
    border: "1px solid rgba(255,255,255,0.16)",
    zIndex: 20,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff",
    marginLeft: 0,
    marginRight: 0,
  }),
  input: (base) => ({
    ...base,
    color: "#fff",
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.75)",
    marginLeft: 0,
    marginRight: 0,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(255,255,255,0.12)"
      : "transparent",
    color: "#fff",
    cursor: "pointer",
  }),
};

function formatCurrency(value) {
  const num = Number(value || 0);
  return num.toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
}

function formatDate(value) {
  if (!value) return "-";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
}

function getDefaultFiltro() {
  return {
    idStagione: "",
    dataDa: "",
    dataA: "",
    tipoMovimento: "",
    categoria: "",
    idCategoriaSportiva: "",
    scuolaCalcio: false,
    agonistica: false,
  };
}

function getEmptyEdit() {
  return {
    id: null,
    idStagione: "",
    dataMovimento: "",
    tipoMovimento: "",
    categoria: "",
    descrizione: "",
    importo: "",
    metodoPagamento: "",
    riferimento: "",
    note: "",
    idCategoriaSportiva: "",
  };
}

function resolveMacroGruppo(values) {
  if (values.idCategoriaSportiva) return "";

  const scuola = Boolean(values.scuolaCalcio);
  const agonistica = Boolean(values.agonistica);

  if (scuola && !agonistica) return "SCUOLA_CALCIO";
  if (!scuola && agonistica) return "AGONISTICA";

  return "";
}

function formatMacroGruppo(value) {
  if (value === "SCUOLA_CALCIO") return "Scuola Calcio";
  if (value === "AGONISTICA") return "Agonistica";
  return "-";
}

export default function AggiornaMovimentiCassa() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [stagioni, setStagioni] = useState([]);
  const [categorieSportive, setCategorieSportive] = useState([]);
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [loadingLista, setLoadingLista] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);

  const [filtri, setFiltri] = useState(getDefaultFiltro());
  const [movimenti, setMovimenti] = useState([]);
  const [riepilogo, setRiepilogo] = useState({
    totaleEntrate: 0,
    totaleUscite: 0,
    saldo: 0,
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(getEmptyEdit());

  useEffect(() => {
    fetchLookups();
    fetchDati(getDefaultFiltro());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchLookups() {
    setLoadingLookup(true);
    try {
      const [resStagioni, resCategorie] = await Promise.all([
        authFetch(`${API_URL}/api/admin/lookup/stagioni`),
        authFetch(`${API_URL}/api/admin/categorie`),
      ]);

      if (!resStagioni.ok) {
        throw new Error("Errore caricamento stagioni");
      }

      if (!resCategorie.ok) {
        throw new Error("Errore caricamento categorie sportive");
      }

      const dataStagioni = await resStagioni.json();
      const dataCategorie = await resCategorie.json();

      const stagioneOptions = (Array.isArray(dataStagioni) ? dataStagioni : []).map((s) => ({
        value: String(s.id),
        label: s.nome || s.descrizione || `Stagione ${s.id}`,
      }));

      const categoriaOptions = [{ value: "", label: "Tutte" }].concat(
        (Array.isArray(dataCategorie) ? dataCategorie : []).map((c) => ({
          value: String(c.id),
          label:
            c.macroGruppo
              ? `${c.nome} • ${formatMacroGruppo(c.macroGruppo)}`
              : c.nome,
        }))
      );

      setStagioni(stagioneOptions);
      setCategorieSportive(categoriaOptions);
    } catch (err) {
      toast.error(err.message || "Errore durante il caricamento dei lookup");
    } finally {
      setLoadingLookup(false);
    }
  }

  function buildQueryString(values) {
    const params = new URLSearchParams();

    if (values.idStagione) params.append("idStagione", values.idStagione);
    if (values.dataDa) params.append("dataDa", values.dataDa);
    if (values.dataA) params.append("dataA", values.dataA);
    if (values.tipoMovimento) params.append("tipoMovimento", values.tipoMovimento);
    if (values.categoria) params.append("categoria", values.categoria);
    if (values.idCategoriaSportiva) {
      params.append("idCategoriaSportiva", values.idCategoriaSportiva);
    }

    const macroGruppoCategoria = resolveMacroGruppo(values);
    if (macroGruppoCategoria) {
      params.append("macroGruppoCategoria", macroGruppoCategoria);
    }

    const query = params.toString();
    return query ? `?${query}` : "";
  }

  async function fetchDati(values = filtri) {
    setLoadingLista(true);
    try {
      const query = buildQueryString(values);

      const [resLista, resRiepilogo] = await Promise.all([
        authFetch(`${API_URL}/api/admin/movimenti-cassa${query}`),
        authFetch(`${API_URL}/api/admin/movimenti-cassa/riepilogo${query}`),
      ]);

      if (!resLista.ok) {
        throw new Error("Errore caricamento movimenti");
      }
      if (!resRiepilogo.ok) {
        throw new Error("Errore caricamento riepilogo");
      }

      const listaData = await resLista.json();
      const riepilogoData = await resRiepilogo.json();

      setMovimenti(Array.isArray(listaData) ? listaData : []);
      setRiepilogo(
        riepilogoData || { totaleEntrate: 0, totaleUscite: 0, saldo: 0 }
      );
    } catch (err) {
      toast.error(err.message || "Errore durante il caricamento dei dati");
    } finally {
      setLoadingLista(false);
    }
  }

  function handleFiltroChange(e) {
    const { name, value, type, checked } = e.target;

    setFiltri((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function handleFiltroSelect(name, selected) {
    setFiltri((prev) => {
      const updated = {
        ...prev,
        [name]: selected?.value || "",
      };

      if (name === "idCategoriaSportiva" && (selected?.value || "")) {
        updated.scuolaCalcio = false;
        updated.agonistica = false;
      }

      return updated;
    });
  }

  async function handleCerca(e) {
    e.preventDefault();
    if (filtri.dataDa && filtri.dataA && filtri.dataDa > filtri.dataA) {
      toast.warning("Intervallo date non valido");
      return;
    }
    await fetchDati(filtri);
  }

  async function handleResetFiltri() {
    const reset = getDefaultFiltro();
    setFiltri(reset);
    await fetchDati(reset);
  }

  async function handleStampaReport() {
    if (filtri.dataDa && filtri.dataA && filtri.dataDa > filtri.dataA) {
      toast.warning("Intervallo date non valido");
      return;
    }

    setPrinting(true);
    try {
      const query = buildQueryString(filtri);
      const res = await authFetch(`${API_URL}/api/admin/movimenti-cassa/pdf${query}`, {
        method: "GET",
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Errore durante la generazione del report PDF");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const suffix =
        filtri.dataDa || filtri.dataA
          ? `_${filtri.dataDa || "inizio"}_${filtri.dataA || "fine"}`
          : "";

      link.download = `report_movimenti_cassa${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report PDF generato con successo");
    } catch (err) {
      toast.error(err.message || "Errore durante la stampa del report");
    } finally {
      setPrinting(false);
    }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setEditForm({
      id: item.id,
      idStagione: item.idStagione ? String(item.idStagione) : "",
      dataMovimento: item.dataMovimento || "",
      tipoMovimento: item.tipoMovimento || "",
      categoria: item.categoria || "",
      descrizione: item.descrizione || "",
      importo: item.importo ?? "",
      metodoPagamento: item.metodoPagamento || "",
      riferimento: item.riferimento || "",
      note: item.note || "",
      idCategoriaSportiva: item.idCategoriaSportiva ? String(item.idCategoriaSportiva) : "",
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(getEmptyEdit());
  }

  function handleEditChange(e) {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleEditSelect(name, selected) {
    setEditForm((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));
  }

  function validateEdit() {
    if (!editForm.idStagione) {
      toast.warning("Seleziona la stagione");
      return false;
    }
    if (!editForm.dataMovimento) {
      toast.warning("Inserisci la data movimento");
      return false;
    }
    if (!editForm.tipoMovimento) {
      toast.warning("Seleziona il tipo movimento");
      return false;
    }
    if (!editForm.categoria) {
      toast.warning("Seleziona la categoria");
      return false;
    }
    if (!editForm.descrizione.trim()) {
      toast.warning("Inserisci la descrizione");
      return false;
    }
    if (!editForm.importo || Number(editForm.importo) <= 0) {
      toast.warning("Inserisci un importo valido");
      return false;
    }
    return true;
  }

  async function saveEdit() {
    if (!validateEdit()) return;

    const payload = {
      idStagione: Number(editForm.idStagione),
      dataMovimento: editForm.dataMovimento,
      tipoMovimento: editForm.tipoMovimento,
      categoria: editForm.categoria,
      descrizione: editForm.descrizione.trim(),
      importo: Number(editForm.importo),
      metodoPagamento: editForm.metodoPagamento || null,
      riferimento: editForm.riferimento.trim() || null,
      note: editForm.note.trim() || null,
      idCategoriaSportiva: editForm.idCategoriaSportiva
        ? Number(editForm.idCategoriaSportiva)
        : null,
    };

    setSaving(true);
    try {
      const res = await authFetch(
        `${API_URL}/api/admin/movimenti-cassa/${editForm.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Errore durante l'aggiornamento");
      }

      await res.json().catch(() => null);

      toast.success("Movimento aggiornato con successo");
      cancelEdit();
      await fetchDati(filtri);
    } catch (err) {
      toast.error(err.message || "Errore durante l'aggiornamento");
    } finally {
      setSaving(false);
    }
  }

  const stagioneFiltroSelected =
    [{ value: "", label: "Tutte" }, ...stagioni].find(
      (s) => s.value === String(filtri.idStagione)
    ) || null;

  const tipoFiltroSelected =
    TIPI_MOVIMENTO.find((t) => t.value === filtri.tipoMovimento) || TIPI_MOVIMENTO[0];

  const categoriaFiltroSelected =
    CATEGORIE.find((c) => c.value === filtri.categoria) || CATEGORIE[0];

  const categoriaSportivaFiltroSelected =
    categorieSportive.find((c) => c.value === String(filtri.idCategoriaSportiva)) || null;

  const stagioneEditSelected =
    stagioni.find((s) => s.value === String(editForm.idStagione)) || null;

  const tipoEditSelected =
    TIPI_MOVIMENTO.filter((t) => t.value !== "").find(
      (t) => t.value === editForm.tipoMovimento
    ) || null;

  const metodoEditSelected =
    METODI_PAGAMENTO.find((m) => m.value === editForm.metodoPagamento) || null;

  const categoriaSportivaEditSelected =
    categorieSportive.find((c) => c.value === String(editForm.idCategoriaSportiva)) || null;

  const categorieEdit = useMemo(() => {
    if (editForm.tipoMovimento === "ENTRATA") {
      return CATEGORIE.filter((c) =>
        [
          "QUOTE",
          "SPONSOR",
          "CONTRIBUTI",
          "EVENTI",
          "RIMBORSI",
          "ALTRE_ENTRATE",
        ].includes(c.value)
      );
    }
    if (editForm.tipoMovimento === "USCITA") {
      return CATEGORIE.filter((c) =>
        [
          "AFFITTO_CAMPI",
          "MATERIALE_SPORTIVO",
          "ARBITRI",
          "RIMBORSI_STAFF",
          "VISITE_MEDICHE",
          "TASSE",
          "FEDERAZIONE",
          "MANUTENZIONE",
          "UTENZE",
          "ALTRE_USCITE",
        ].includes(c.value)
      );
    }
    return [];
  }, [editForm.tipoMovimento]);

  const categoriaEditSelected =
    categorieEdit.find((c) => c.value === editForm.categoria) || null;

  return (
    <div className="sfondo-immagine">
      <div className="agg-movcassa-container">
        <h2 className="agg-movcassa-title">Aggiorna Movimenti di Cassa</h2>

        <form className="agg-movcassa-filtri" onSubmit={handleCerca}>
          <div className="agg-movcassa-grid agg-movcassa-grid--tre">
            <div>
              <label>Stagione</label>
              <Select
                options={[{ value: "", label: "Tutte" }, ...stagioni]}
                value={stagioneFiltroSelected}
                onChange={(selected) => handleFiltroSelect("idStagione", selected)}
                isClearable={false}
                isDisabled={loadingLookup}
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label>Data da</label>
              <input
                type="date"
                name="dataDa"
                value={filtri.dataDa}
                onChange={handleFiltroChange}
              />
            </div>

            <div>
              <label>Data a</label>
              <input
                type="date"
                name="dataA"
                value={filtri.dataA}
                onChange={handleFiltroChange}
              />
            </div>
          </div>

          <div className="agg-movcassa-grid agg-movcassa-grid--due">
            <div>
              <label>Tipologia movimento</label>
              <Select
                options={TIPI_MOVIMENTO}
                value={tipoFiltroSelected}
                onChange={(selected) =>
                  handleFiltroSelect("tipoMovimento", selected)
                }
                isClearable={false}
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label>Categoria contabile</label>
              <Select
                options={CATEGORIE}
                value={categoriaFiltroSelected}
                onChange={(selected) => handleFiltroSelect("categoria", selected)}
                isClearable={false}
                styles={customSelectStyles}
              />
            </div>
          </div>

          <div className="agg-movcassa-grid agg-movcassa-grid--due">
            <div>
              <label>Categoria sportiva</label>
              <Select
                options={categorieSportive}
                value={categoriaSportivaFiltroSelected}
                onChange={(selected) => handleFiltroSelect("idCategoriaSportiva", selected)}
                isClearable={false}
                styles={customSelectStyles}
              />
            </div>

            <div className="agg-movcassa-macro-wrap">
              <label>Macro gruppo</label>
              <div className="agg-movcassa-check-row">
                <label className="agg-movcassa-check">
                  <input
                    type="checkbox"
                    name="scuolaCalcio"
                    checked={filtri.scuolaCalcio}
                    onChange={handleFiltroChange}
                    disabled={Boolean(filtri.idCategoriaSportiva)}
                  />
                  <span>Scuola Calcio</span>
                </label>

                <label className="agg-movcassa-check">
                  <input
                    type="checkbox"
                    name="agonistica"
                    checked={filtri.agonistica}
                    onChange={handleFiltroChange}
                    disabled={Boolean(filtri.idCategoriaSportiva)}
                  />
                  <span>Agonistica</span>
                </label>
              </div>
            </div>
          </div>

          <div className="agg-movcassa-actions agg-movcassa-actions--filtri">
            <button
              type="button"
              className="agg-movcassa-btn agg-movcassa-btn--ghost"
              onClick={handleResetFiltri}
            >
              Reset filtri
            </button>

            <button
              type="button"
              className="agg-movcassa-btn agg-movcassa-btn--ghost"
              onClick={handleStampaReport}
              disabled={printing}
            >
              {printing ? "Generazione report..." : "Stampa report"}
            </button>

            <button
              type="submit"
              className="agg-movcassa-btn agg-movcassa-btn--primary"
            >
              Cerca
            </button>
          </div>
        </form>

        <div className="agg-movcassa-summary">
          <div className="agg-movcassa-summary-card">
            <span className="agg-movcassa-summary-label">Totale entrate</span>
            <strong>{formatCurrency(riepilogo.totaleEntrate)}</strong>
          </div>

          <div className="agg-movcassa-summary-card">
            <span className="agg-movcassa-summary-label">Totale uscite</span>
            <strong>{formatCurrency(riepilogo.totaleUscite)}</strong>
          </div>

          <div className="agg-movcassa-summary-card agg-movcassa-summary-card--saldo">
            <span className="agg-movcassa-summary-label">Saldo</span>
            <strong>{formatCurrency(riepilogo.saldo)}</strong>
          </div>
        </div>

        {loadingLista ? (
          <div className="agg-movcassa-empty">Caricamento movimenti...</div>
        ) : movimenti.length === 0 ? (
          <div className="agg-movcassa-empty">
            Nessun movimento trovato con i filtri selezionati
          </div>
        ) : (
          <div className="agg-movcassa-list">
            {movimenti.map((item) => {
              const isEditing = editingId === item.id;

              return (
                <div className="agg-movcassa-item" key={item.id}>
                  {!isEditing ? (
                    <>
                      <div className="agg-movcassa-item-top">
                        <div className="agg-movcassa-item-main">
                          <h3 className="agg-movcassa-item-title">
                            {item.tipoMovimento === "ENTRATA" ? "Entrata" : "Uscita"} — {item.categoria}
                          </h3>
                          <p className="agg-movcassa-item-meta">
                            {formatDate(item.dataMovimento)} • {item.nomeStagione}
                          </p>
                          {(item.nomeCategoriaSportiva || item.macroGruppoCategoriaSportiva) && (
                            <p className="agg-movcassa-item-meta">
                              {item.nomeCategoriaSportiva || "-"}
                              {item.macroGruppoCategoriaSportiva
                                ? ` • ${formatMacroGruppo(item.macroGruppoCategoriaSportiva)}`
                                : ""}
                            </p>
                          )}
                        </div>

                        <div className="agg-movcassa-item-right">
                          <div className="agg-movcassa-item-amount">
                            {formatCurrency(item.importo)}
                          </div>
                          <button
                            type="button"
                            className="agg-movcassa-btn agg-movcassa-btn--ghost agg-movcassa-btn--small"
                            onClick={() => startEdit(item)}
                          >
                            Modifica
                          </button>
                        </div>
                      </div>

                      <div className="agg-movcassa-item-details-row">
                        <div className="agg-movcassa-inline-detail agg-movcassa-inline-detail--descrizione">
                          <span className="agg-movcassa-detail-label">Descrizione</span>
                          <span className="agg-movcassa-detail-value">{item.descrizione || "-"}</span>
                        </div>

                        <div className="agg-movcassa-inline-detail">
                          <span className="agg-movcassa-detail-label">Metodo</span>
                          <span className="agg-movcassa-detail-value">{item.metodoPagamento || "-"}</span>
                        </div>

                        <div className="agg-movcassa-inline-detail">
                          <span className="agg-movcassa-detail-label">Riferimento</span>
                          <span className="agg-movcassa-detail-value">{item.riferimento || "-"}</span>
                        </div>
                      </div>

                      {item.note && (
                        <div className="agg-movcassa-note-row">
                          <span className="agg-movcassa-detail-label">Note</span>
                          <span className="agg-movcassa-detail-value">{item.note}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="agg-movcassa-edit-head">
                        <div>
                          <h3 className="agg-movcassa-item-title">Modifica movimento</h3>
                          <p className="agg-movcassa-item-meta">ID movimento: {item.id}</p>
                        </div>
                      </div>

                      <div className="agg-movcassa-grid agg-movcassa-grid--due">
                        <div>
                          <label>Stagione</label>
                          <Select
                            options={stagioni}
                            value={stagioneEditSelected}
                            onChange={(selected) =>
                              handleEditSelect("idStagione", selected)
                            }
                            isClearable
                            styles={customSelectStyles}
                          />
                        </div>

                        <div>
                          <label>Data movimento</label>
                          <input
                            type="date"
                            name="dataMovimento"
                            value={editForm.dataMovimento}
                            onChange={handleEditChange}
                          />
                        </div>
                      </div>

                      <div className="agg-movcassa-grid agg-movcassa-grid--due">
                        <div>
                          <label>Tipologia movimento</label>
                          <Select
                            options={TIPI_MOVIMENTO.filter((t) => t.value !== "")}
                            value={tipoEditSelected}
                            onChange={(selected) => {
                              setEditForm((prev) => ({
                                ...prev,
                                tipoMovimento: selected?.value || "",
                                categoria: "",
                              }));
                            }}
                            isClearable
                            styles={customSelectStyles}
                          />
                        </div>

                        <div>
                          <label>Categoria contabile</label>
                          <Select
                            options={categorieEdit}
                            value={categoriaEditSelected}
                            onChange={(selected) =>
                              handleEditSelect("categoria", selected)
                            }
                            isClearable
                            isDisabled={!editForm.tipoMovimento}
                            styles={customSelectStyles}
                          />
                        </div>
                      </div>

                      <div className="agg-movcassa-grid agg-movcassa-grid--due">
                        <div>
                          <label>Categoria sportiva</label>
                          <Select
                            options={categorieSportive.filter((c) => c.value !== "")}
                            value={categoriaSportivaEditSelected}
                            onChange={(selected) =>
                              handleEditSelect("idCategoriaSportiva", selected)
                            }
                            isClearable
                            styles={customSelectStyles}
                          />
                        </div>

                        <div>
                          <label>Metodo pagamento</label>
                          <Select
                            options={METODI_PAGAMENTO}
                            value={metodoEditSelected}
                            onChange={(selected) =>
                              handleEditSelect("metodoPagamento", selected)
                            }
                            isClearable
                            styles={customSelectStyles}
                          />
                        </div>
                      </div>

                      <div className="agg-movcassa-grid agg-movcassa-grid--due">
                        <div>
                          <label>Importo</label>
                          <input
                            type="number"
                            name="importo"
                            value={editForm.importo}
                            onChange={handleEditChange}
                            min="0"
                            step="0.01"
                          />
                        </div>

                        <div>
                          <label>Riferimento</label>
                          <input
                            type="text"
                            name="riferimento"
                            value={editForm.riferimento}
                            onChange={handleEditChange}
                            maxLength={100}
                          />
                        </div>
                      </div>

                      <div className="agg-movcassa-grid agg-movcassa-grid--uno">
                        <div>
                          <label>Descrizione</label>
                          <input
                            type="text"
                            name="descrizione"
                            value={editForm.descrizione}
                            onChange={handleEditChange}
                            maxLength={255}
                          />
                        </div>
                      </div>

                      <div className="agg-movcassa-grid agg-movcassa-grid--uno">
                        <div>
                          <label>Note</label>
                          <input
                            type="text"
                            name="note"
                            value={editForm.note}
                            onChange={handleEditChange}
                            maxLength={500}
                          />
                        </div>
                      </div>

                      <div className="agg-movcassa-actions">
                        <button
                          type="button"
                          className="agg-movcassa-btn agg-movcassa-btn--ghost"
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          Annulla
                        </button>

                        <button
                          type="button"
                          className="agg-movcassa-btn agg-movcassa-btn--primary"
                          onClick={saveEdit}
                          disabled={saving}
                        >
                          {saving ? "Salvataggio..." : "Salva modifica"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="agg-movcassa-actions agg-movcassa-actions--footer">
          <button
            type="button"
            className="agg-movcassa-btn agg-movcassa-btn--ghost"
            onClick={() => navigate("/gestionale")}
          >
            Torna al gestionale
          </button>

          <button
            type="button"
            className="agg-movcassa-btn agg-movcassa-btn--primary"
            onClick={() => navigate("/gestionale/admin/movimenti-cassa")}
          >
            Vai a nuovo movimento
          </button>
        </div>
      </div>
    </div>
  );
}